import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const BeforeAfter = () => {
  const { t } = useTranslation();

  return (
    <section
      id="work"
      data-testid="before-after-section"
      className="py-14 lg:py-20 relative overflow-hidden bg-[#FAF8F5]"
      aria-label="Before and after case study"
    >
      {/* Animated background typography */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        <motion.div className="absolute whitespace-nowrap" style={{ top: '8%' }}
          animate={{ x: ['5%', '-35%'] }} transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[14vw] font-bold text-[#1A1816]/[0.025] tracking-[-0.05em] font-sans leading-none">
            BEFORE&nbsp;&nbsp;&nbsp;AFTER&nbsp;&nbsp;&nbsp;RESULTS&nbsp;&nbsp;&nbsp;BEFORE&nbsp;&nbsp;&nbsp;AFTER
          </span>
        </motion.div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-[1px] bg-[#C9A96E]" />
            <span className="text-[9px] font-semibold tracking-[0.3em] uppercase text-[#8A857D]">{t('beforeafter.label')}</span>
            <div className="w-10 h-[1px] bg-[#C9A96E]" />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-bold text-[#1A1816]">
            {t('beforeafter.title1')} <span className="font-serif italic text-[#C9A96E]">{t('beforeafter.title2')}</span>
          </h2>
        </motion.div>

        {/* Before/After Image */}
        <motion.div className="relative max-w-4xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <div className="relative overflow-hidden" data-testid="before-after-slider">
            <img
              src="https://www.amst.com/user/files/before-after%20(1).jpg"
              alt="Website redesign before and after comparison showing transformation from outdated design to modern high-converting layout"
              className="w-full h-auto"
              loading="lazy"
            />
            <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-[#1A1816]/80 text-[#FAF8F5] px-3 py-1.5 sm:px-4 sm:py-2 text-[10px] sm:text-xs tracking-wider uppercase backdrop-blur-sm">
              {t('beforeafter.before')}
            </div>
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-[#C9A96E] text-[#1A1816] px-3 py-1.5 sm:px-4 sm:py-2 text-[10px] sm:text-xs tracking-wider uppercase font-semibold">
              {t('beforeafter.after')}
            </div>
          </div>

          {/* Info */}
          <motion.div className="mt-6 text-center"
            initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <p className="text-sm sm:text-base text-[#8A857D]">{t('beforeafter.desc')}</p>
            <p className="text-xs sm:text-sm text-[#C9A96E] font-semibold tracking-wide mt-1">{t('beforeafter.tags')}</p>
          </motion.div>

          {/* Stats */}
          <motion.div className="mt-10 grid grid-cols-3 gap-6 text-center"
            initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.3 }}>
            {[['3x', t('beforeafter.s1')], ['65%', t('beforeafter.s2')], ['2x', t('beforeafter.s3')]].map(([num, label]) => (
              <div key={num}>
                <span className="text-3xl sm:text-4xl lg:text-5xl font-serif italic text-[#C9A96E]">{num}</span>
                <p className="text-[10px] sm:text-xs text-[#8A857D] mt-1 tracking-wide">{label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default BeforeAfter;
